import React, { useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import Tooltip from '@atlaskit/tooltip';
import Toggle from '@atlaskit/toggle';
import { emptyStateProp, errorStateProp } from '@css-xp/ppc-common';
import { BillingAdminPerson, CustomDynamicResultTable, TableBody } from '@css-xp/base';
import { BillingAdminEdge } from '@css-xp/ppc-graphtypes';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';
import {
  NotificationLevel,
  NotificationPreferenceTypes,
  useBillingAdminsPreferencesHandler,
} from './use-billing-admins-preference-handler';
import { PreferencesCell } from './preferences-cell';
import { AdminAvatarGroup } from '../show-avatar-group';
import './billing-and-licensing-emails.scss';

export interface Admin {
  fullName: string;
  email: string;
  isPartnerAdmin: boolean;
  picture: string;
  atlassianAccountId: string;
}

export interface BillingAdminsPreferences extends Admin {
  isEnabled: boolean;
  level: string | null;
}

export type BillingAndLicensingEmailsSettingsProps = {
  billingAdminIds?: string[];
  areBillingAdminIdsBeingFetched?: boolean;
  errorHappenedWhileFetchingBillingAdminIds?: ApolloError | undefined;
  shouldManageBillingProfileNotifications?: boolean;
};

export const BillingAndLicensingEmailsSettings: React.FC<BillingAndLicensingEmailsSettingsProps> = ({
  areBillingAdminIdsBeingFetched = false,
  billingAdminIds = [],
  errorHappenedWhileFetchingBillingAdminIds,
  shouldManageBillingProfileNotifications = false,
}) => {
  const {
    data,
    loading,
    error,
    getAdminsPreferencesByTxaId,
    setAdminsPreferences,
  } = useBillingAdminsPreferencesHandler(billingAdminIds);
  const [selectedAdmins, setSelectedAdmins] = useState<Admin[]>([]);
  const [billingAdminsPreferences, setBillingAdminsPreferences] = useState<BillingAdminsPreferences[]>([]);
  const [isPreferencesLoading, setPreferencesLoading] = useState<boolean>(false);
  const { txaId } = useParams<{ txaId: string }>();

  const billingAdminsList = useMemo(() => {
    const billingAdmins = data?.transactionAccount?.billingAdmins?.edges || [];
    return billingAdmins.map((billingAdmin: BillingAdminEdge | null) => ({
      fullName: billingAdmin?.node?.fullName ?? '',
      email: billingAdmin?.node?.email ?? '',
      isPartnerAdmin: billingAdmin?.node.isPartnerAdmin ?? false,
      picture: billingAdmin?.node.picture ?? '',
      atlassianAccountId: billingAdmin?.node.atlassianAccountId ?? '',
    }));
  }, [data]);

  const hasNoResults = !!error || !!errorHappenedWhileFetchingBillingAdminIds;

  const isAdvocateAllowedToManagerNotificationPreferences = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.NOTIFICATION_PREFERENCES,
  });

  useEffect(() => {
    setPreferencesLoading(true);
    // checking if data is neither null nor undefined beforehand, so that it would not cause unnecessary re-renders and should only render once the data is available.
    if (data) {
      (async () => {
        const billingAdminsPreferences = await getAdminsPreferencesByTxaId(
          NotificationPreferenceTypes?.EMAIL,
          shouldManageBillingProfileNotifications
        );

        if (shouldManageBillingProfileNotifications) {
          const adminsPreferences = billingAdminsList?.map((billingAdmin) => {
            const adminPreference = billingAdminsPreferences?.find(
              (admin: any) => admin?.userAaid === billingAdmin?.atlassianAccountId
            );
            return {
              ...billingAdmin,
              isEnabled: adminPreference?.isEnabled ?? true,
              level: adminPreference?.level ?? null,
            };
          });
          setBillingAdminsPreferences(adminsPreferences as BillingAdminsPreferences[]);
        } else {
          // Filter out the admins who prefer not to receive notifications from the billingAdminsList
          const receivingEmailsAdmins = billingAdminsList?.filter(
            (admin) => !billingAdminsPreferences?.includes(admin.atlassianAccountId)
          );
          setSelectedAdmins(receivingEmailsAdmins as Admin[]);
        }
        setPreferencesLoading(false);
      })();
    }
  }, [billingAdminsList, txaId]);

  const handleToggle = async (admin: Admin) => {
    const prevSelectedAdmins = [...selectedAdmins];
    if (
      selectedAdmins.some((item) => item.atlassianAccountId === admin.atlassianAccountId) &&
      selectedAdmins?.length !== 1
    ) {
      setSelectedAdmins(selectedAdmins.filter((a) => a.atlassianAccountId !== admin.atlassianAccountId));
      const result = await setAdminsPreferences(admin?.atlassianAccountId, NotificationPreferenceTypes.EMAIL, false);
      // revert the state if api fails
      !result.success && setSelectedAdmins(prevSelectedAdmins);
    } else {
      setSelectedAdmins([...selectedAdmins, admin]);
      const result = await setAdminsPreferences(admin?.atlassianAccountId, NotificationPreferenceTypes.EMAIL, true);
      // revert the state if api fails
      !result.success &&
        setSelectedAdmins(selectedAdmins.filter((a) => a.atlassianAccountId !== admin.atlassianAccountId));
    }
  };

  const renderSingleAdminTooltipText = <p>At least one team member needs to receive billing and licensing emails.</p>;

  const renderBillingAndLicensingText = (
    <p className="btm-padding top-padding">
      At least one billing admin needs to receive billing and licensing emails. If they're no longer able to, another
      <br />
      billing admin must be selected to receive them - so be sure to keep email addresses and preferences up to date.
      <br /> <br />
      You may still get marketing, identity, or other emails that aren't covered by this page's settings, in accordance{' '}
      <br />
      with your other preferences and Atlassian policies. <br />
      <br />
      Soon, you'll be able to customize the types of billing and licensing emails you receive.
    </p>
  );

  const adminsList = shouldManageBillingProfileNotifications ? billingAdminsPreferences : billingAdminsList;

  const getProps = () => ({
    children: (
      <>
        <TableBody>
          {adminsList?.map((admin, index) => {
            let hasSingleAdminLeft = false;
            if (shouldManageBillingProfileNotifications) {
              const activeNotificationsAdmins = billingAdminsPreferences?.filter((admin) => {
                return admin?.isEnabled || admin?.level === NotificationLevel?.INVOICE_GROUP;
              });
              hasSingleAdminLeft =
                activeNotificationsAdmins?.length === 1 && activeNotificationsAdmins?.[0]?.email === admin?.email;
            } else {
              hasSingleAdminLeft = selectedAdmins.length === 1 && selectedAdmins?.[0]?.email === admin?.email;
            }
            const billingAdmin = billingAdminsPreferences?.find(
              (billingAdmin) => billingAdmin?.atlassianAccountId === admin?.atlassianAccountId
            );
            return (
              <tr key={index}>
                <td>
                  <BillingAdminPerson
                    fullName={admin.fullName}
                    email={admin.email}
                    isPartnerAdmin={admin.isPartnerAdmin}
                    picture={admin.picture}
                  />
                </td>
                <td
                  className={classNames(
                    { 'trigger-icon': !shouldManageBillingProfileNotifications },
                    {
                      'dropdown-cell-preferences': shouldManageBillingProfileNotifications,
                    }
                  )}
                >
                  <Tooltip content={hasSingleAdminLeft ? renderSingleAdminTooltipText : ''}>
                    {shouldManageBillingProfileNotifications ? (
                      <PreferencesCell
                        billingAdmin={billingAdmin}
                        setPreferencesLoading={setPreferencesLoading}
                        setBillingAdminsPreferences={setBillingAdminsPreferences}
                        billingAdminsPreferences={billingAdminsPreferences}
                        selectedAdmin={admin}
                        isDisabled={hasSingleAdminLeft || !isAdvocateAllowedToManagerNotificationPreferences}
                        updateAdminsPreferences={setAdminsPreferences}
                      />
                    ) : (
                      <Toggle
                        id="toggle-default"
                        onChange={() => handleToggle(admin)}
                        isDisabled={hasSingleAdminLeft || !isAdvocateAllowedToManagerNotificationPreferences}
                        testId={`manage-${admin?.email}-notifications`}
                        isChecked={selectedAdmins.some(
                          (selectedAdmin) => selectedAdmin.atlassianAccountId === admin.atlassianAccountId
                        )}
                      />
                    )}
                  </Tooltip>
                </td>
              </tr>
            );
          })}
        </TableBody>
      </>
    ),
    noResults: hasNoResults,
    isLoading: isPreferencesLoading || loading || areBillingAdminIdsBeingFetched,
    error: errorHappenedWhileFetchingBillingAdminIds?.toString() ?? error?.toString(),
    emptyStateProps: emptyStateProp,
    errorStateProps: errorStateProp,
  });

  return (
    <div className="notifications-setting-billing-admins-container">
      <div className="billing-and-licensing-container">
        <h2 className="billing-and-licensing-container-heading btm-padding">Billing and licensing email settings</h2>
        <AdminAvatarGroup
          selectedAdmins={selectedAdmins}
          billingAdminsPreferences={billingAdminsPreferences}
          shouldManageBillingProfileNotifications={shouldManageBillingProfileNotifications}
        />
      </div>
      {renderBillingAndLicensingText}
      <CustomDynamicResultTable {...getProps()}></CustomDynamicResultTable>
    </div>
  );
};
